import { useBreakpointValue } from "@gluestack-style/react";
import { Box, FlatList, View } from "components/ui";
import React, { useCallback } from "react";
import { ActivityIndicator, useWindowDimensions } from "react-native";
import { useIsMobile } from "utils/hooks/useIsMobile";
import ItemCard from "./ItemCard";
import ItemListItem from "./ItemListItem";

type CategoryCardListProps = {
	list?: any;
	isGridView?: boolean;
	header?: any;
	scrollEnabled?: boolean;
	byCategory?: boolean;
	isOnNavigation?: boolean;
	onEndReached?: () => void;
	hasMore?: boolean;
	location?: number | null;
	isOnSearch?: boolean;
};

const ItemCardList = React.memo(function ItemCardList({ list, isGridView = true, byCategory = false, header, scrollEnabled, isOnNavigation = false, onEndReached, hasMore, location = null, isOnSearch = false }: CategoryCardListProps) {
	const { width: windowWidth } = useWindowDimensions();
	const isMobile = useIsMobile();
	const noColumn = useBreakpointValue({
		base: 2,
		lg: 3,
	});
	const itemWidth = useBreakpointValue({
		base: "$1/2",
		lg: "$1/3",
	});

	// Memoize the renderItem function to prevent recreation on every render
	const renderItem = useCallback(
		({ item, index }: any) => {
			return (
				<Box key={`item-${item.stockcode || item.id || index}`} position="relative" zIndex={1} width={isGridView ? itemWidth : "$full"}>
					{isGridView && <ItemCard isOnNavigation={isOnNavigation} item={item} isOnSearch={isOnSearch} />}
					{!isGridView && <ItemListItem item={item} location={location} isOnSearch={isOnSearch} />}
				</Box>
			);
		},
		[isGridView, itemWidth, isOnNavigation, location, isOnSearch],
	);

	// Memoize the keyExtractor function
	const keyExtractor = useCallback((item: any, index: number) => {
		return item.stockcode || item.id || `item-${index}`;
	}, []);

	if (!list) {
		return null;
	}

	// Assume each CategoryCard has a fixed height, for example, 400px
	const itemHeight = isGridView ? 667 : 177;
	const gap = 8;
	const numberOfRows = isGridView ? Math.ceil(list.length / noColumn) : list.length;
	const totalGapSpace = isGridView ? (numberOfRows - 1) * gap : 0;
	const dynamicHeight = numberOfRows * itemHeight + totalGapSpace;

	const renderFooter = () => {
		if (!hasMore) return null;
		return (
			<Box paddingVertical={20} alignItems="center">
				<ActivityIndicator size="large" color="#1A428A" />
			</Box>
		);
	};

	function Wrapper(props: any) {
		if (isMobile) {
			return props.children;
		}

		return (
			<View>
				<Box>{props.children}</Box>
			</View>
		);
	}

	return (
		<Wrapper>
			<FlatList
				contentContainerStyle={{ width: "100%", backgroundColor: "#FAFAFA" }}
				key={isGridView ? noColumn : 1}
				data={list}
				height={"$full"}
				scrollEnabled={scrollEnabled}
				numColumns={isGridView ? noColumn : 1}
				ListHeaderComponent={header}
				ListHeaderComponentStyle={{ zIndex: 9 }}
				ListFooterComponent={renderFooter}
				onEndReached={onEndReached}
				onEndReachedThreshold={0.5}
				renderItem={renderItem}
				keyExtractor={keyExtractor}
			/>
		</Wrapper>
	);
});

export default ItemCardList;
